<script lang="ts" setup>
import { syncRefs, useUrlSearchParams } from '@vueuse/core';
import { XButton, XIcon, XAlert, XLink } from '@clicksign/excalibur';
import { useToast } from 'vue-toastification';
import { useQueryClient } from '@tanstack/vue-query';
import { enum as zodEnum } from 'zod';

import { simpleMask, HELP_LINKS, isFetchError, useFeature, Decisions } from '~~core';

import { tokenQueryKeys, useFetchTokenExpiration } from '../../../../../repositories/token';
import { useFetchRequirement } from '../../../../../repositories/requirement';
import TokenRequirementSkeleton from '../../../../../components/TokenRequirementSkeleton/TokenRequirementSkeleton.vue';
import { useFetchSigner } from '../../../../../repositories/signer';
import { saveRequirement, resendToken } from '../../../../../services';
import { OtpInput, ExpirationTokenMessage, RequirementAdvanceButton } from '../../../../../components';
import { useRequirementStep } from '../../../../../composables/use-requirement-step';
import { useTokenResend } from '../../../../../composables/use-token-resend';
import { usePresetToken } from '../../../../../composables/use-preset-token';
import { useRequirement } from '../../../../../composables/use-requirement';
import { useWebotp } from '../../../../../composables/use-webotp';

function validateTokenKindParam(value: unknown) {
  const validTokenKindsSchema = zodEnum(['whatsapp', 'sms', 'email']);
  const parseResult = validTokenKindsSchema.safeParse(value);

  if (!parseResult.success) throw createError({ fatal: true, statusCode: 404 });

  return parseResult.data;
}

function useTokenChannel() {
  const route = useRoute();
  /// @ts-expect-error Nuxt typed routes is experimental and cant infer the param "tokenKind" at the current version
  const tokenChannel = computed(() => validateTokenKindParam(route.params.tokenKind));
  const isPhonePreferredChannel = computed(() => ['whatsapp', 'sms'].includes(tokenChannel.value));

  return {
    tokenChannel,
    isPhonePreferredChannel,
  };
}

const { t } = useI18n();
const toast = useToast();
const queryClient = useQueryClient();
const { code, abort } = useWebotp();
const { presetToken } = usePresetToken();
const { tokenChannel, isPhonePreferredChannel } = useTokenChannel();
const searchParams = useUrlSearchParams();
const isLoading = ref(true);
const { isEnabled: isToggleEmbedToken } = useFeature(Decisions.EMAIL_EMBED_TOKEN);
const { finishRequirement, signatureRequestKey, setActiveRequirement } = useRequirementStep();
const { requirement, refetch: refetchTokenStep } = useFetchRequirement(tokenChannel, signatureRequestKey);
const { signer } = useFetchSigner(signatureRequestKey);
const { isFilled } = useRequirement(requirement);
const { expirationInfo } = useFetchTokenExpiration(signatureRequestKey, tokenChannel);

setActiveRequirement(tokenChannel.value);

const validUntilParse = computed(() => ({
  remainingAttempts: 1,
  blockedUntil: expirationInfo.value.validUntil,
}));
const expirationData = computed(() => (expirationInfo.value.used ? validUntilParse.value : expirationInfo.value.resend));
const { hasAttempts, timerStr, isInCooldown } = useTokenResend(expirationData);

const token = ref(presetToken.value);
const isSubmitting = ref(false);
const isResendingNewToken = ref(false);
const hasResentToken = ref(false);
const errorMessage = ref('');
const title = t('requirement.token.title');

useHead({ title });
syncRefs(presetToken, token);

const showRemainingAttemptsAlert = computed(() => tokenChannel.value === 'whatsapp' && hasResentToken.value);
const tokenSentTo = computed(() => (isPhonePreferredChannel.value ? simpleMask(signer.value?.phoneNumber ?? '', '(##) #####-####') : signer.value?.email));
const showResendButton = computed(() => !isInCooldown.value && !isFilled.value && !(isToggleEmbedToken.value && presetToken.value));

const inputLabel = computed(() => {
  let locale = 'default';

  if (presetToken.value) locale = 'presetToken';
  if (hasResentToken.value) locale = 'resentToken';

  return t(`requirement.token.description.${locale}`);
});

const isSubmitDisabled = computed(() => {
  const normalCheck = token.value.length < 6 || isResendingNewToken.value;

  if (expirationInfo.value.used) {
    return isInCooldown.value ? false : normalCheck;
  }

  return normalCheck && !isFilled.value;
});

async function newToken() {
  try {
    hasResentToken.value = false;
    isResendingNewToken.value = true;

    const expiration = await resendToken(signatureRequestKey.value, tokenChannel.value);
    const { queryKey } = tokenQueryKeys.show(signatureRequestKey.value, tokenChannel.value);

    hasResentToken.value = true;

    await queryClient.cancelQueries(queryKey);
    queryClient.setQueryData(queryKey, expiration);
    toast.success(t('requirement.token.resend.success'));
    token.value = '';
    errorMessage.value = '';
  } catch (e) {
    toast.error(t('requirement.token.resend.error'));

    if (!isFetchError(e)) throw e;
  } finally {
    isResendingNewToken.value = false;
  }
}

const receivedTokenFromWebotp = computed(() => tokenChannel.value === 'sms' && code.value !== '');

async function saveTokenRequirement(query?: { webotp: boolean }) {
  await saveRequirement(
    tokenChannel.value,
    signatureRequestKey.value,
    { code: token.value },
    query,
  );
}

async function completeRequirement() {
  if (isFilled.value) return finishRequirement();

  const query = receivedTokenFromWebotp.value ? { webotp: receivedTokenFromWebotp.value } : undefined;

  try {
    isSubmitting.value = true;
    await saveTokenRequirement(query);
    finishRequirement();
  } catch (error) {
    errorMessage.value = t('requirement.token.description.error');
    toast.error(errorMessage.value);
    isSubmitting.value = false;

    if (!isFetchError(error)) throw error;
  }
}

watch(
  () => code.value,
  () => {
    if (code.value !== '') {
      token.value = code.value;
      searchParams.token_sms = code.value;
    }
    if (hasResentToken.value) toast.success(t('requirement.token.autofill'));
  },
  { immediate: true },
);

onMounted(async () => {
  const shouldAutoFulfill = (isToggleEmbedToken.value && !!presetToken.value) || expirationInfo.value.used;

  if (!isFilled.value && shouldAutoFulfill) {
    try {
      await saveTokenRequirement();
      await refetchTokenStep();
    } catch {
      if (!receivedTokenFromWebotp.value) {
        token.value = '';
        presetToken.value = '';
      }
    }
  }
  isLoading.value = false;
});

onUnmounted(() => {
  abort();
});
</script>

<template>
  <section
    v-if="isLoading"
    :class="$style.tokenSkeleton"
  >
    <TokenRequirementSkeleton />
  </section>
  <section
    v-else
    :class="$style.token"
  >
    <XIcon
      :class="$style.icon"
      icon="shield-check"
      size="lg"
    />

    <h3>{{ title }}</h3>

    <XAlert
      v-if="isFilled && isToggleEmbedToken"
      :class="$style.alert"
      icon="shield-check"
      type="success"
      full-width
    >
      {{ t('requirement.token.autoFullfil') }}
    </XAlert>
    <XAlert
      v-else-if="!!presetToken"
      :class="$style.alert"
      icon="shield-check"
    >
      {{ t('requirement.token.autofill') }}
    </XAlert>
    <p
      v-if="!isFilled"
      :class="$style.description"
    >
      {{ t('requirement.token.sent', { contact: tokenSentTo }) }}
    </p>

    <form
      :class="$style.form"
      @submit.prevent="completeRequirement"
    >
      <OtpInput
        v-if="!(isFilled && isToggleEmbedToken)"
        v-model="token"
        :label="inputLabel"
        :length="6"
        :disabled="(isInCooldown && expirationInfo.used) || isFilled"
        :error="errorMessage"
        :token-channel="tokenChannel"
        data-testid="tokenEmailField"
        @update:model-value="token = $event"
      />

      <RequirementAdvanceButton
        type="submit"
        :loading="isSubmitting"
        :disabled="isSubmitDisabled"
        full-width
      />

      <ExpirationTokenMessage
        v-if="!isFilled && isInCooldown && expirationInfo.used"
        :timer-str="timerStr"
        authenticated-token
      />
    </form>

    <XAlert
      :class="$style.alert"
      :is-visible="showRemainingAttemptsAlert"
      type="warning"
    >
      {{ t('requirement.token.resend.remainingAttemps', { length: expirationInfo?.resend.remainingAttempts }, expirationInfo?.resend.remainingAttempts || 0) }}
    </XAlert>

    <XLink
      v-if="!hasAttempts && showRemainingAttemptsAlert"
      :class="$style.link"
      target="_blank"
      :to="HELP_LINKS.tokenAttempts"
    >
      {{ t('requirement.token.help') }}
    </XLink>

    <ExpirationTokenMessage
      v-if="isInCooldown && hasAttempts && !expirationInfo.used"
      :timer-str="timerStr"
    />

    <XButton
      v-if="showResendButton"
      design="link"
      :loading="isResendingNewToken"
      :disabled="isSubmitting || !hasAttempts || isInCooldown"
      data-testid="resendTokenLink"
      @click="newToken"
    >
      {{ t('requirement.token.resend.action', { channel: tokenChannel }) }}

      <XIcon
        icon="chevron-right"
        right
      />
    </XButton>
  </section>
</template>

<style lang="scss" module>
.token {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  max-width: var(--step-max-width);
  margin: auto;
  padding: var(--space-medium);
  text-align: center;
  color: var(--color-neutral-900);
}

@media (min-width: 768px) {
  .icon {
    margin-bottom: var(--space-medium);
  }
}

.alert {
  margin-bottom: var(--space-medium);
}

.label,
.link {
  margin-bottom: var(--space-regular);
}

.description {
  font-weight: var(--font-weight-semibold);
}

.tokenSkeleton {
  width: 100%;
  margin: auto;

  @include breakpoint('medium') {
    max-width: 450px;
  }
}

.form {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  margin-bottom: var(--space-large);
  border: none;
}
</style>
